import JSZip from 'jszip'
import {OptimizeBoqImageBlob} from '@/helpers/boqImageCompression.js'

const OFFICE_RELATIONSHIPS_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships'

const parseXml = xml => {
    const document = new DOMParser().parseFromString(xml, 'application/xml')
    if (document.getElementsByTagName('parsererror').length) throw new Error('The workbook contains invalid XML.')
    return document
}

const getRelationshipId = element =>
    element.getAttributeNS(OFFICE_RELATIONSHIPS_NS, 'id') || element.getAttribute('r:id')

const getRelationships = async (zip, sourcePath) => {
    const slash = sourcePath.lastIndexOf('/')
    const directory = slash < 0 ? '' : sourcePath.slice(0, slash + 1)
    const filename = sourcePath.slice(slash + 1)
    const relsPath = directory + '_rels/' + filename + '.rels'
    const relsFile = zip.file(relsPath)
    if (!relsFile) return []

    const document = parseXml(await relsFile.async('string'))
    return Array.from(document.getElementsByTagName('*'))
        .filter(element => element.localName === 'Relationship')
        .map(element => ({
            id: element.getAttribute('Id'),
            type: element.getAttribute('Type'),
            target: element.getAttribute('Target')
        }))
}

const resolvePackageTarget = (sourcePath, target) => {
    const decoded = decodeURIComponent(String(target || '')).replace(/\\/g, '/')
    const parts = decoded.startsWith('/')
        ? []
        : sourcePath.split('/').slice(0, -1)
    for (const part of decoded.split('/')) {
        if (!part || part === '.') continue
        if (part === '..') parts.pop()
        else parts.push(part)
    }
    return parts.join('/')
}

const getTemplateImagePaths = async zip => {
    const workbookPath = 'xl/workbook.xml'
    const workbookFile = zip.file(workbookPath)
    if (!workbookFile) return new Set()

    const workbook = parseXml(await workbookFile.async('string'))
    const templateSheet = Array.from(workbook.getElementsByTagName('*'))
        .find(element => element.localName === 'sheet' && element.getAttribute('name')?.toLowerCase() === 'template')
    const sheetId = templateSheet && getRelationshipId(templateSheet)
    if (!sheetId) return new Set()

    const workbookRelationships = await getRelationships(zip, workbookPath)
    const worksheetRelationship = workbookRelationships.find(relationship => relationship.id === sheetId)
    if (!worksheetRelationship?.target) return new Set()
    const worksheetPath = resolvePackageTarget(workbookPath, worksheetRelationship.target)
    const worksheetRelationships = await getRelationships(zip, worksheetPath)
    const drawingRelationships = worksheetRelationships.filter(relationship => relationship.type?.endsWith('/drawing'))
    const imagePaths = new Set()

    for (const relationship of drawingRelationships) {
        if (!relationship.target) continue
        const drawingPath = resolvePackageTarget(worksheetPath, relationship.target)
        const drawingFile = zip.file(drawingPath)
        if (!drawingFile) continue
        const drawingDocument = parseXml(await drawingFile.async('string'))
        const imageRelationships = await getRelationships(zip, drawingPath)

        for (const anchor of Array.from(drawingDocument.getElementsByTagName('*'))
            .filter(element => ['oneCellAnchor', 'twoCellAnchor'].includes(element.localName))) {
            const from = Array.from(anchor.getElementsByTagName('*')).find(element => element.localName === 'from')
            const rowElement = from && Array.from(from.getElementsByTagName('*')).find(element => element.localName === 'row')
            const row = Number(rowElement?.textContent)
            if (!Number.isInteger(row) || row < 1) continue

            const blips = Array.from(anchor.getElementsByTagName('*')).filter(element => element.localName === 'blip')
            for (const blip of blips) {
                const relationshipId = blip.getAttributeNS(OFFICE_RELATIONSHIPS_NS, 'embed') || blip.getAttribute('r:embed')
                const imageRelationship = imageRelationships.find(item => item.id === relationshipId && item.type?.endsWith('/image'))
                if (imageRelationship?.target) imagePaths.add(resolvePackageTarget(drawingPath, imageRelationship.target))
            }
        }
    }
    return imagePaths
}

// Only optimize images linked from the Template sheet. Keep package paths and
// image formats unchanged so workbook drawing references and PNG transparency survive.
export const OptimizeBoqWorkbookImages = async file => {
    const zip = await JSZip.loadAsync(file)
    const mediaPaths = await getTemplateImagePaths(zip)
    let changed = false

    for (const path of mediaPaths) {
        const match = path.match(/\.([^.]+)$/)
        const extension = match?.[1]?.toLowerCase()
        if (!['png', 'jpg', 'jpeg'].includes(extension)) continue

        const mediaFile = zip.file(path)
        if (!mediaFile) continue
        const source = await mediaFile.async('blob')
        if (source.size === 0) continue
        const optimized = await OptimizeBoqImageBlob(source, extension === 'png' ? 'png' : 'jpeg')
        if (optimized && optimized.size < source.size) {
            zip.file(path, optimized, {binary: true})
            changed = true
        }
    }

    if (!changed) return file
    const workbook = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {level: 3},
        mimeType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    if (workbook.size >= file.size) return file
    return new File([workbook], file.name, {type: file.type, lastModified: file.lastModified})
}
